"use client";

import { motion, useSpring, useTransform, useScroll } from "framer-motion";
import { useState, useRef, useEffect, Fragment } from "react";
import {
  Search,
  FileText,
  Globe,
  Image as ImageIcon,
  Music,
  Activity,
  FileBarChart,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import Image from "next/image";
import { SpringElement } from "@/components/ui/spring-element";
import { TextEffect } from "@/components/ui/text-effect";
import { TextMorph } from "@/components/ui/text-morph";

interface ChromeBrowserSectionProps {
  onDownloadClick?: () => void;
  onShowHeaderChange?: (show: boolean) => void;
}

export function ChromeBrowserSection({ onDownloadClick, onShowHeaderChange }: ChromeBrowserSectionProps = {}) {
  const [activeTab, setActiveTab] = useState(0);
  const [isThinkingExpanded, setIsThinkingExpanded] = useState(false);
  const [showPill, setShowPill] = useState(false);
  const [pillMessage, setPillMessage] = useState("");
  const [viewportHeight, setViewportHeight] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const googleVideoRef = useRef<HTMLVideoElement>(null);
  const notionVideoRef = useRef<HTMLVideoElement>(null);
  const redditVideoRef = useRef<HTMLVideoElement>(null);
  const xVideoRef = useRef<HTMLVideoElement>(null);
  const backgroundReady = true; // Background is handled externally

  // Design constants
  const DESIGN_BROWSER_HEIGHT = 700;

  // Calculate scale factor based on viewport height
  const scaleFactor = viewportHeight < 800 ? 0.8 : 1;
  const scaledBrowserHeight = DESIGN_BROWSER_HEIGHT * scaleFactor;

  useEffect(() => {
    const updateViewportHeight = () => {
      setViewportHeight(window.innerHeight);
    };
    updateViewportHeight();
    window.addEventListener("resize", updateViewportHeight);
    return () => window.removeEventListener("resize", updateViewportHeight);
  }, []);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  // Monitor scroll progress and show/hide header
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    // 延迟订阅，确保 Lenis 已经初始化
    const timeoutId = setTimeout(() => {
      unsubscribe = scrollYProgress.on("change", (progress) => {
        if (onShowHeaderChange) {
          onShowHeaderChange(progress > 0.1);
        }
      });
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [scrollYProgress, onShowHeaderChange]);

  // Browser Y position - calculated based on design specifications
  const availableHeight = viewportHeight - 60;
  const distanceFromTop = 514; // Distance from header to center position
  const m = distanceFromTop + scaledBrowserHeight / 2; // 514 + (scaled browser height / 2)
  const targetY = availableHeight * 0.5 - m + 60;
  const browserY = useTransform(scrollYProgress, [0, 0.2], [0, targetY]);

  // Browser rotation - starts tilted and straightens up
  const browserRotateX = useTransform(scrollYProgress, [0, 0.5], [12, 0]);

  // Google page content opacity - disappears before tabs start changing
  const googleContentOpacity = useTransform(scrollYProgress, [0.2, 0.4], [1, 0]);

  // Chrome browser opacity - stays visible throughout
  const chromeOpacity = useTransform(
    scrollYProgress,
    [0, 1], // Main animation → End
    [1, 1] // Visible → Visible (no fade out)
  );

  // Second phase: Tab transformations and other elements (0.45-0.55)
  const googleTabWidth = useSpring(useTransform(scrollYProgress, [0.45, 0.55, 0.8], [200, 400, 400]), {
    stiffness: 100,
    damping: 30,
  });
  const googleTabHeight = useSpring(useTransform(scrollYProgress, [0.45, 0.55, 0.8], [28, 300, 300]), {
    stiffness: 100,
    damping: 30,
  });
  const googleTabY = useSpring(useTransform(scrollYProgress, [0.45, 0.55, 0.8], [0, 510, 510]), {
    stiffness: 100,
    damping: 30,
  });
  const googleTabX = useSpring(useTransform(scrollYProgress, [0.45, 0.55, 0.8], [0, -83, -83]), {
    stiffness: 100,
    damping: 30,
  });

  // Notion tab transformation - move to right side of Google page
  const notionTabWidth = useSpring(useTransform(scrollYProgress, [0.45, 0.55, 0.8], [200, 400, 400]), {
    stiffness: 100,
    damping: 30,
  });
  const notionTabHeight = useSpring(useTransform(scrollYProgress, [0.45, 0.55, 0.8], [28, 300, 300]), {
    stiffness: 100,
    damping: 30,
  });
  const notionTabY = useSpring(useTransform(scrollYProgress, [0.45, 0.55, 0.8], [0, 510, 510]), {
    stiffness: 100,
    damping: 30,
  });
  const notionTabX = useSpring(useTransform(scrollYProgress, [0.45, 0.55, 0.8], [0, -76, -76]), {
    stiffness: 100,
    damping: 30,
  });

  // Reddit tab transformation - move to bottom left
  const redditTabWidth = useSpring(useTransform(scrollYProgress, [0.45, 0.55, 0.8], [200, 400, 400]), {
    stiffness: 100,
    damping: 30,
  });
  const redditTabHeight = useSpring(useTransform(scrollYProgress, [0.45, 0.55, 0.8], [28, 300, 300]), {
    stiffness: 100,
    damping: 30,
  });
  const redditTabY = useSpring(useTransform(scrollYProgress, [0.45, 0.55, 0.8], [0, 190, 190]), {
    stiffness: 100,
    damping: 30,
  });
  const redditTabX = useSpring(useTransform(scrollYProgress, [0.45, 0.55, 0.8], [0, -908, -908]), {
    stiffness: 100,
    damping: 30,
  });

  // X tab transformation - move to bottom right
  const xTabWidth = useSpring(useTransform(scrollYProgress, [0.45, 0.55, 0.8], [200, 400, 400]), {
    stiffness: 100,
    damping: 30,
  });
  const xTabHeight = useSpring(useTransform(scrollYProgress, [0.45, 0.55, 0.8], [28, 300, 300]), {
    stiffness: 100,
    damping: 30,
  });
  const xTabY = useSpring(useTransform(scrollYProgress, [0.45, 0.55, 0.8], [0, 190, 190]), {
    stiffness: 100,
    damping: 30,
  });
  const xTabX = useSpring(useTransform(scrollYProgress, [0.45, 0.55, 0.8], [0, -900, -900]), {
    stiffness: 100,
    damping: 30,
  });

  // Plus tab animation - positioning and width expansion
  const plusTabX = useSpring(useTransform(scrollYProgress, [0.45, 0.55, 0.8], [0, -1650, -1650]), {
    stiffness: 100,
    damping: 30,
  });
  const plusTabWidth = useSpring(useTransform(scrollYProgress, [0.45, 0.6, 0.8], [32, 200, 200]), {
    stiffness: 100,
    damping: 30,
  });

  // Window controls animation - physically move from tab bar to navigation bar
  const windowControlsY = 0;

  // Tab bar height shrinking animation - starts after tab transformations
  const tabBarHeight = 36;

  // First phase: Browser scale shrinking animation (0.55-0.65)
  const browserScaleX = useSpring(useTransform(scrollYProgress, [0, 0.55, 0.65, 0.8], [1, 1, 0.98, 0.98]), {
    stiffness: 100,
    damping: 30,
  });
  const browserScaleY = useSpring(useTransform(scrollYProgress, [0, 0.55, 0.65, 0.8], [1, 1, 0.973, 0.973]), {
    stiffness: 100,
    damping: 30,
  });

  // Second phase: Browser width reduction animation (0.55-0.65) - actual width change
  const browserWidth = useSpring(useTransform(scrollYProgress, [0, 0.55, 0.65, 0.8], ["100%", "100%", "75%", "75%"]), {
    stiffness: 100,
    damping: 30,
  });

  // Background gray frame opacity - appears when browser starts shrinking
  const backgroundFrameOpacity = useSpring(useTransform(scrollYProgress, [0, 0.55, 0.65, 0.8], [0, 0, 1, 1]), {
    stiffness: 100,
    damping: 30,
  });

  // Sidebar animation - appears from right when browser shrinks
  const sidebarX = useSpring(useTransform(scrollYProgress, [0, 0.55, 0.65, 0.8], [300, 300, 0, 0]), {
    stiffness: 100,
    damping: 30,
  });
  const sidebarOpacity = useSpring(useTransform(scrollYProgress, [0, 0.55, 0.65, 0.8], [0, 0, 1, 1]), {
    stiffness: 100,
    damping: 30,
  });

  // User Query animation - appears after sidebar is visible
  const userQueryOpacity = useSpring(useTransform(scrollYProgress, [0, 0.62, 0.65, 0.8], [0, 0, 1, 1]), {
    stiffness: 100,
    damping: 30,
  });
  const userQueryX = useSpring(useTransform(scrollYProgress, [0, 0.62, 0.65, 0.8], [50, 50, 0, 0]), {
    stiffness: 100,
    damping: 30,
  });

  // Task cards scroll-based animations - appear after user query is in place (0.66-0.8)
  const googleTaskOpacity = useSpring(useTransform(scrollYProgress, [0, 0.66, 0.68, 0.8], [0, 0, 1, 1]), {
    stiffness: 100,
    damping: 30,
  });
  const googleTaskY = useSpring(useTransform(scrollYProgress, [0, 0.66, 0.68, 0.8], [10, 10, 0, 0]), {
    stiffness: 100,
    damping: 30,
  });

  const xTaskOpacity = useSpring(useTransform(scrollYProgress, [0, 0.68, 0.7, 0.8], [0, 0, 1, 1]), {
    stiffness: 100,
    damping: 30,
  });
  const xTaskY = useSpring(useTransform(scrollYProgress, [0, 0.68, 0.7, 0.8], [10, 10, 0, 0]), {
    stiffness: 100,
    damping: 30,
  });

  const redditTaskOpacity = useSpring(useTransform(scrollYProgress, [0, 0.71, 0.73, 0.8], [0, 0, 1, 1]), {
    stiffness: 100,
    damping: 30,
  });
  const redditTaskY = useSpring(useTransform(scrollYProgress, [0, 0.71, 0.73, 0.8], [10, 10, 0, 0]), {
    stiffness: 100,
    damping: 30,
  });

  const notionTaskOpacity = useSpring(useTransform(scrollYProgress, [0, 0.74, 0.76, 0.8], [0, 0, 1, 1]), {
    stiffness: 100,
    damping: 30,
  });
  const notionTaskY = useSpring(useTransform(scrollYProgress, [0, 0.74, 0.76, 0.8], [10, 10, 0, 0]), {
    stiffness: 100,
    damping: 30,
  });

  // Tab content opacity transforms
  const googleTabContentOpacity = useTransform(googleTabHeight, [28, 100, 300], [1, 0, 0]);
  const notionTabContentOpacity = useTransform(notionTabHeight, [28, 100, 300], [1, 0, 0]);
  const redditTabContentOpacity = useTransform(redditTabHeight, [28, 100, 300], [1, 0, 0]);
  const xTabContentOpacity = useTransform(xTabHeight, [28, 100, 300], [1, 0, 0]);

  // Track task cards visibility state
  const [taskCardsVisible, setTaskCardsVisible] = useState(false);
  const [pillAnimationComplete, setPillAnimationComplete] = useState(false);

  // Plus tab text transformation based on scroll progress
  const plusTabShowText = useTransform(scrollYProgress, [0.45, 0.6], [0, 1]);
  const plusTabHideSymbol = useTransform(plusTabShowText, [0, 1], [1, 0]);

  // Plus tab background and shadow transforms
  const plusTabBackgroundColor = useTransform(plusTabShowText, [0, 1], ["rgba(0,0,0,0)", "white"]);
  const plusTabBoxShadow = useTransform(plusTabShowText, [0, 1], ["none", "0px 0px 2px 0px rgba(150,148,156,0.42)"]);

  // Use setTimeout to control video timing - independent of Chrome/Fellou animations
  useEffect(() => {
    const timer = setTimeout(() => {
      setTaskCardsVisible(true);
    }, 1600); // 1.4s (last card) + 0.2s buffer

    return () => clearTimeout(timer);
  }, []);

  // Track pill animation completion - after all animations including description
  useEffect(() => {
    if (!backgroundReady) return;

    const timer = setTimeout(() => {
      setPillAnimationComplete(true);
    }, 2200); // After description animation (2.0s) + small buffer

    return () => clearTimeout(timer);
  }, [backgroundReady]);

  // Tab video opacity transforms - based on task cards visibility and tab height
  const googleTabVideoOpacity = useTransform(googleTabHeight, [28, 100, 300], [0, 0, taskCardsVisible ? 1 : 0]);
  const notionTabVideoOpacity = useTransform(notionTabHeight, [28, 100, 300], [0, 0, taskCardsVisible ? 1 : 0]);
  const redditTabVideoOpacity = useTransform(redditTabHeight, [28, 100, 300], [0, 0, taskCardsVisible ? 1 : 0]);
  const xTabVideoOpacity = useTransform(xTabHeight, [28, 100, 300], [0, 0, taskCardsVisible ? 1 : 0]);

  // Combined Y animations for tabs (original movement only)
  const googleTabYCombined = googleTabY;
  const notionTabYCombined = notionTabY;
  const redditTabYCombined = redditTabY;
  const xTabYCombined = xTabY;

  const tabs = [
    { title: "Google", favicon: "https://api.iconify.design/logos:google-icon.svg", isActive: true },
    { title: "Notion", favicon: "https://api.iconify.design/logos:notion-icon.svg", isActive: false },
    { title: "Reddit", favicon: "https://api.iconify.design/logos:reddit-icon.svg", isActive: false },
    { title: "X", favicon: "https://api.iconify.design/logos:x.svg", isActive: false },
    { title: "+", favicon: "", isActive: false, isNewTab: true },
  ];

  // Monitor task cards state and control video playback
  useEffect(() => {
    const videos = [googleVideoRef, notionVideoRef, redditVideoRef, xVideoRef];

    if (taskCardsVisible) {
      // When task cards appear and effect is enabled, play all videos
      videos.forEach((videoRef) => {
        if (videoRef.current) {
          videoRef.current.play();
        }
      });
    } else {
      // When task cards are not visible or effect is disabled, pause all videos
      videos.forEach((videoRef) => {
        if (videoRef.current) {
          videoRef.current.pause();
        }
      });
    }
  }, [taskCardsVisible]);

  return (
    <>
      <div ref={containerRef} className="h-[550vh] bg-transparent relative z-20">
        <motion.div
          className="sticky top-0 h-screen flex flex-col p-8"
          style={{
            opacity: chromeOpacity,
          }}
        >
          {/* Ambient light background effect */}

          {/* Fellou Title - Top Half */}
          <motion.div
            className="flex-1 flex flex-col items-center justify-end pt-16 text-center mb-20"
            style={{
              opacity: useTransform(scrollYProgress, [0, 0.15], [1, 0]),
            }}
          >
            {/* Combined Title and Download Button with Sequential Animation - Original Pill */}
            <motion.div
              className="flex items-center mb-8 bg-gray-400/20 backdrop-blur-sm rounded-full border border-gray-300/30 py-1"
              style={{
                overflow: pillAnimationComplete ? "visible" : "hidden",
              }}
              initial={{ opacity: 0, filter: "blur(4px)" }}
              animate={{ opacity: backgroundReady ? 1 : 0, filter: backgroundReady ? "blur(0px)" : "blur(4px)" }}
              transition={{ delay: backgroundReady ? 0.5 : 0 }}
            >
              {/* Fellou Title - appears first */}
              <motion.div
                layoutId="fellou-logo-section"
                className="flex items-center gap-3 px-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: backgroundReady ? 1 : 0 }}
                transition={{
                  default: { delay: backgroundReady ? 0.7 : 0 },
                  layout: { delay: 0 },
                }}
              >
                <div className="relative">
                  <motion.div layoutId="fellou-logo">
                    <Image
                      src="/favicon.ico"
                      alt="Fellou Logo"
                      width={32}
                      height={32}
                      unoptimized
                      className="w-8 h-8 relative z-10 drop-shadow-2xl"
                    />
                  </motion.div>
                </div>
                <motion.h1 layoutId="fellou-text" className="text-2xl font-bold text-white">
                  Fellou
                </motion.h1>
              </motion.div>
            </motion.div>

            <h1 className="text-8xl font-bold text-white pointer-events-auto mb-4">
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: backgroundReady ? 1 : 0 }}
                transition={{ delay: backgroundReady ? 0.8 : 0 }}
              >
                The W
                <SpringElement
                  className="text-7xl relative -top-[3px] inline-block"
                  springClassName="stroke-white dark:stroke-white stroke-[4px]"
                  dragElastic={0.3}
                  springConfig={{ stiffness: 150, damping: 30 }}
                  springPathConfig={{
                    coilCount: 6,
                    amplitudeMin: 10,
                    amplitudeMax: 25,
                    curveRatioMin: 0.6,
                    curveRatioMax: 1.2,
                    bezierOffset: 12,
                  }}
                >
                  <span aria-label="O" className="block select-none">
                    🌎
                  </span>
                </SpringElement>
                rld&apos;s{" "}
              </motion.span>
              <motion.span
                initial={{ x: -200, opacity: 0, scale: 0.8, filter: "blur(4px)", skewX: 10 }}
                animate={{
                  x: backgroundReady ? 0 : -200,
                  opacity: backgroundReady ? 1 : 0,
                  scale: backgroundReady ? 1 : 0.8,
                  filter: backgroundReady ? "blur(0px)" : "blur(4px)",
                  skewX: backgroundReady ? 0 : 0,
                }}
                transition={{ delay: backgroundReady ? 1.1 : 0 }}
                className="inline-block"
              >
                First
              </motion.span>
              <br />
              <TextEffect
                className="flex justify-center font-serif"
                per="char"
                preset="fade-in-blur"
                delay={1.4}
                speedSegment={2}
                variants={{
                  item: {
                    hidden: {
                      opacity: 0,
                      filter: "blur(10px) brightness(0%)",
                      y: 30,
                      scale: 0.8,
                      rotateX: 15,
                    },
                    visible: {
                      opacity: 1,
                      filter: "blur(0px) brightness(100%)",
                      y: 0,
                      scale: 1,
                      rotateX: 0,
                      transition: {
                        duration: 0.6,
                        ease: "easeOut",
                      },
                    },
                    exit: {
                      opacity: 0,
                      filter: "blur(10px) brightness(0%)",
                      y: -30,
                      scale: 0.8,
                      rotateX: -15,
                    },
                  },
                }}
              >
                Agentic Browser
              </TextEffect>
            </h1>

            {/* Description */}
            <motion.p
              className="text-lg text-white/40 max-w-2xl mb-4"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: backgroundReady ? 1 : 0, y: backgroundReady ? 0 : 20 }}
              transition={{ delay: backgroundReady ? 2.0 : 0, duration: 0.6 }}
            >
              Go beyond browsing with Fellou AI Browser that takes automated web action
            </motion.p>
          </motion.div>

          {/* Browser - Bottom Half, moves from bottom to center */}
          <motion.div
            className="flex-1 flex items-center justify-center"
            style={{
              y: browserY,
              rotateX: browserRotateX,
              scale: scaleFactor,
              transformOrigin: "center center",
              transformPerspective: 1000,
            }}
          >
            <div className="relative w-full max-w-6xl h-[700px]">
              {/* Background gray frame - appears when browser shrinks */}
              <motion.div
                className="absolute inset-0 bg-gray-400/20 rounded-lg border border-gray-300/30 overflow-hidden"
                style={{
                  opacity: backgroundFrameOpacity,
                }}
              >
                {/* Sidebar - New Figma Design */}
                <motion.div
                  className="absolute right-0 top-0 w-[300px] h-full rounded-[16px] "
                  style={{
                    x: sidebarX,
                    opacity: sidebarOpacity,
                  }}
                >
                  <div className="h-full flex shrink-0">
                    {/* Vertical Navigation Bar - Left Side */}
                    <div className="flex flex-col items-center py-2 shrink-0 pl-3">
                      {/* Top Section - Logo and Gift */}
                      <div className="flex flex-col items-center gap-6">
                        {/* Fellou Logo */}
                        <div className="w-6 h-6">
                          <Image
                            src="/favicon.ico"
                            alt="Fellou"
                            width={24}
                            height={24}
                            unoptimized
                          />
                        </div>

                        {/* Gift Icon with background */}
                        <div className="bg-[rgba(255,255,255,0.08)] rounded-[8px] w-6 h-6 flex items-center justify-center">
                          <Image src="/gift_fill.svg" alt="Gift" width={16} height={16} />
                        </div>
                      </div>

                      {/* Center Section - Navigation Icons */}
                      <div className="flex flex-col items-center gap-6 flex-1 justify-center">
                        {/* Home Icon */}
                        <div className="w-3 h-[13px] relative">
                          <Image src="/home_1_fill.svg" alt="Home" width={12} height={13} />
                        </div>

                        {/* Dot Icon */}
                        <div className="w-3 h-3 relative">
                          <div className="absolute inset-0 w-3 h-3">
                            <div className="w-full h-full bg-white/20 rounded-full"></div>
                            <div className="absolute left-[1px] top-[1px] w-2.5 h-2.5 bg-white rounded-full"></div>
                          </div>
                        </div>

                        {/* Add Icon */}
                        <div className="w-3 h-3 relative">
                          <div className="absolute inset-0 w-3 h-3">
                            <div className="w-full h-full bg-white/20 rounded-full"></div>
                            <div className="absolute left-0.5 top-0.5 w-2 h-2">
                              <Image src="/add_icon.svg" alt="Add" width={8} height={8} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Main Content Area */}
                    <div className="flex-1 flex flex-col">
                      {/* Header */}
                      <div className="flex items-center justify-between px-3 pt-2 pb-3">
                        <div className="bg-[rgba(255,255,255,0.08)] flex items-center gap-1 px-2 py-0.5 rounded-[99px]">
                          <Image src="/lightning_line.svg" alt="Lightning" width={16} height={16} />
                          <span className="text-white text-[12px] font-medium leading-[20px]">1000</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <button className="p-1 rounded hover:bg-white/10 transition-colors">
                            <Image src="/Frame 1261153968.svg" alt="New Chat" width={20} height={20} />
                          </button>
                          <button className="p-1 rounded hover:bg-white/10 transition-colors">
                            <Image src="/Frame 1261153972.svg" alt="Timer" width={20} height={20} />
                          </button>
                          <button className="p-1 rounded hover:bg-white/10 transition-colors">
                            <Image src="/Frame 1261153971.svg" alt="Close" width={20} height={20} />
                          </button>
                        </div>
                      </div>

                      {/* Content Area */}
                      <div className="flex-1 overflow-hidden relative px-3">
                        {/* User Message */}
                        <motion.div
                          className="flex justify-end mb-4"
                          style={{
                            opacity: userQueryOpacity,
                            x: userQueryX,
                          }}
                        >
                          <div className="bg-[rgba(152,122,255,0.32)] px-4 py-2 rounded-bl-[12px] rounded-tl-[12px] rounded-tr-[12px] max-w-[300px]">
                            <p className="text-white text-[14px] leading-[20px] fellou-buddy-text">
                              Fellou Help me automated all my online grunt work
                            </p>
                          </div>
                        </motion.div>

                        {/* Thinking Section */}
                        <div className="mb-2">
                          <button
                            className="flex items-center gap-2 mb-2 w-full text-left hover:opacity-80 transition-opacity"
                            onClick={() => setIsThinkingExpanded(!isThinkingExpanded)}
                          >
                            <Image src="/check_circle_line.svg" alt="Check" width={16} height={16} />
                            <span className="text-[#c8c6cd] text-[12px] leading-[20px]">Thinking</span>
                            <motion.svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="#c8c6cd"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              animate={{ rotate: isThinkingExpanded ? 180 : 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              <polyline points="6 9 12 15 18 9" />
                            </motion.svg>
                          </button>
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{
                              height: isThinkingExpanded ? "auto" : 0,
                              opacity: isThinkingExpanded ? 1 : 0,
                            }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                            style={{ overflow: "hidden" }}
                          >
                            <div className="mb-2">
                              <p className="text-[rgba(255,255,255,0.6)] text-[12px] leading-[20px] border-l border-[rgba(255,255,255,0.24)] pl-4">
                                Breaking down competitor analysis: identify key players, gather pricing data, analyze
                                feature sets, compile market positioning insights...
                              </p>
                            </div>
                          </motion.div>
                        </div>

                        <p className="text-white text-[14px] leading-[22px] mb-2">
                          No problem at all, that&apos;s easy for me.
                        </p>

                        {/* Deep Action Badge and Title */}
                        <div className="mb-6">
                          <div className="bg-[rgba(102,201,255,0.24)] inline-flex items-center px-2 py-0.5 rounded-br-[8px] rounded-tl-[8px] rounded-tr-[8px] mb-2">
                            <span className="text-[#66c9ff] text-[10px] leading-[14px] font-bold">
                              Deep Action Agent
                            </span>
                          </div>
                          <h3 className="text-white text-[16px] leading-[28px] font-semibold">
                            24/7 AI Browser Is Getting Things Done
                          </h3>
                        </div>

                        {/* Running Tasks */}
                        <div className="mb-6">
                          <p className="text-[rgba(255,255,255,0.6)] text-[14px] leading-[22px] mb-4">Running Tasks:</p>
                          <div className="space-y-2">
                            <motion.div
                              className="bg-[rgba(255,255,255,0.08)] flex items-center gap-2 h-10 px-2 py-1 rounded-[8px]"
                              style={{
                                opacity: googleTaskOpacity,
                                y: googleTaskY,
                              }}
                            >
                              <div className="flex items-center justify-center w-7 h-7 rounded-[6px]">
                                <Image
                                  src="https://api.iconify.design/logos:google-icon.svg"
                                  alt="Google"
                                  width={18}
                                  height={18}
                                />
                              </div>
                              <span className="text-white text-[11px] leading-[16px]">Competitor pricing</span>
                            </motion.div>

                            <motion.div
                              className="bg-[rgba(255,255,255,0.08)] flex items-center gap-2 h-10 px-2 py-1 rounded-[8px]"
                              style={{
                                opacity: xTaskOpacity,
                                y: xTaskY,
                              }}
                            >
                              <div className="flex items-center justify-center w-7 h-7 rounded-[6px]">
                                <Image src="https://api.iconify.design/logos:x.svg" alt="X" width={18} height={18} />
                              </div>
                              <span className="text-white text-[11px] leading-[16px]">Feature comparison</span>
                            </motion.div>

                            <motion.div
                              className="bg-[rgba(255,255,255,0.08)] flex items-center gap-2 h-10 px-2 py-1 rounded-[8px]"
                              style={{
                                opacity: redditTaskOpacity,
                                y: redditTaskY,
                              }}
                            >
                              <div className="flex items-center justify-center w-7 h-7 rounded-[6px]">
                                <Image
                                  src="https://api.iconify.design/logos:reddit-icon.svg"
                                  alt="Reddit"
                                  width={18}
                                  height={18}
                                />
                              </div>
                              <span className="text-white text-[11px] leading-[16px]">Market positioning</span>
                            </motion.div>

                            <motion.div
                              className="bg-[rgba(255,255,255,0.08)] flex items-center gap-2 h-10 px-2 py-1 rounded-[8px]"
                              style={{
                                opacity: notionTaskOpacity,
                                y: notionTaskY,
                              }}
                            >
                              <div className="flex items-center justify-center w-7 h-7 rounded-[6px]">
                                <Image
                                  src="https://api.iconify.design/logos:notion-icon.svg"
                                  alt="Notion"
                                  width={18}
                                  height={18}
                                />
                              </div>
                              <span className="text-white text-[11px] leading-[16px]">Report compilation</span>
                            </motion.div>
                          </div>
                        </div>
                      </div>

                      {/* Bottom Input */}
                      <div className="px-4 pb-4">
                        {/* Input Field */}
                        <motion.div
                          className="backdrop-blur-sm bg-[rgba(255,255,255,0.08)] border border-[rgba(255,255,255,0.24)] p-4 rounded-[12px] flex flex-col gap-8"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.4, delay: 1.8 }}
                        >
                          <p className="text-[rgba(255,255,255,0.6)] text-[14px] leading-[18px]">Download to use</p>
                          <div className="flex items-center justify-between h-6">
                            <div className="flex items-center gap-4">
                              <button className="border border-[rgba(255,255,255,0.16)] p-[3px] rounded-[99px]">
                                <svg
                                  width="18"
                                  height="18"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="white"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <line x1="12" y1="5" x2="12" y2="19" />
                                  <line x1="5" y1="12" x2="19" y2="12" />
                                </svg>
                              </button>
                              <button className="p-[3px] rounded-[99px]">
                                <span className="text-white text-lg">@</span>
                              </button>
                            </div>
                            <button
                              className="bg-[rgba(255,255,255,0.08)] p-[2px] rounded-[6px] w-6 h-6 flex items-center justify-center hover:bg-[rgba(255,255,255,0.12)] transition-colors cursor-pointer"
                              onClick={onDownloadClick}
                            >
                              <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="white"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <polyline points="9 10 4 15 9 20" />
                                <path d="M20 4v7a4 4 0 0 1-4 4H4" />
                              </svg>
                            </button>
                          </div>
                        </motion.div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>

              {/* Browser container */}
              <motion.div
                className="w-full h-full rounded-2xl shadow-2xl overflow-hidden flex flex-col relative z-10"
                initial={{ opacity: 0, scale: 0.9, y: 50 }}
                whileInView={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                viewport={{ once: true }}
                style={{
                  scaleX: browserScaleX,
                  scaleY: browserScaleY,
                  width: browserWidth,
                  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1)",
                }}
              >
                {/* Tab Bar with Window Controls */}
                <motion.div
                  className=" flex items-center justify-between backdrop-blur-[50px] rounded-tl-[16px] rounded-tr-[16px] shadow-[0px_1px_0px_0px_rgba(209,209,209,0.32)]"
                  style={{
                    height: tabBarHeight,
                    backgroundColor: "rgba(255, 255, 255, 0.86)",
                  }}
                >
                  <div className="flex items-center h-full">
                    {/* Navigation Controls - physically moving */}
                    <motion.div
                      className="flex items-center space-x-1 px-4 relative z-20"
                      style={{
                        y: windowControlsY,
                      }}
                    >
                      <button
                        className="w-8 h-8 rounded-[8px] hover:bg-gray-100/50 flex items-center justify-center cursor-pointer p-2"
                        onClick={() => {
                          setPillMessage("Why cant we go backward");
                          setShowPill(true);
                          setTimeout(() => setShowPill(false), 1500);
                        }}
                      >
                        <ChevronLeft size={16} className="text-gray-400" />
                      </button>
                      <button
                        className="w-8 h-8 rounded-[8px] hover:bg-gray-100/50 flex items-center justify-center cursor-pointer p-2"
                        onClick={() => {
                          setPillMessage("things move forward");
                          setShowPill(true);
                          setTimeout(() => setShowPill(false), 1500);
                        }}
                      >
                        <ChevronRight size={16} className="text-gray-400" />
                      </button>
                    </motion.div>

                    {/* Tabs container with light background */}
                    <div className="flex h-full gap-1 px-1 items-center">
                      {tabs.map((tab, index) => (
                        <Fragment key={`tab-${index}`}>
                          <motion.div
                            key={index}
                            className={`flex items-center px-2 cursor-pointer relative h-[28px] ${
                              tab.isNewTab
                                ? "rounded-[8px] mx-0.5 transition-colors duration-300"
                                : index === activeTab
                                ? "bg-white rounded-[8px] mx-0.5 shadow-[0px_0px_2px_0px_rgba(150,148,156,0.42)]"
                                : "rounded-[8px] mx-0.5 hover:bg-gray-100/50"
                            }`}
                            onClick={() => !tab.isNewTab && setActiveTab(index)}
                            style={
                              index === 0
                                ? {
                                    width: googleTabWidth,
                                    height: googleTabHeight,
                                    y: googleTabYCombined,
                                    x: googleTabX,
                                    zIndex: 10,
                                  }
                                : index === 1
                                ? {
                                    width: notionTabWidth,
                                    height: notionTabHeight,
                                    y: notionTabYCombined,
                                    x: notionTabX,
                                    zIndex: 9,
                                  }
                                : index === 2
                                ? {
                                    width: redditTabWidth,
                                    height: redditTabHeight,
                                    y: redditTabYCombined,
                                    x: redditTabX,
                                    zIndex: 8,
                                  }
                                : index === 3
                                ? {
                                    width: xTabWidth,
                                    height: xTabHeight,
                                    y: xTabYCombined,
                                    x: xTabX,
                                    zIndex: 7,
                                  }
                                : index === 4
                                ? {
                                    x: plusTabX,
                                    width: plusTabWidth,
                                    zIndex: 6,
                                    backgroundColor: plusTabBackgroundColor,
                                    boxShadow: plusTabBoxShadow,
                                  }
                                : {}
                            }
                          >
                            {!tab.isNewTab && (
                              <>
                                {/* When tab is normal size */}
                                <motion.div
                                  className="flex items-center w-full group"
                                  style={{
                                    opacity:
                                      index === 0
                                        ? googleTabContentOpacity
                                        : index === 1
                                        ? notionTabContentOpacity
                                        : index === 2
                                        ? redditTabContentOpacity
                                        : xTabContentOpacity,
                                  }}
                                >
                                  <Image
                                    src={tab.favicon}
                                    alt={tab.title}
                                    width={16}
                                    height={16}
                                    className="mr-2 shrink-0"
                                  />
                                  <span className="text-sm truncate text-gray-700 flex-1">{tab.title}</span>

                                  {/* Hover buttons */}
                                  <div className="flex items-center gap-1 ml-auto opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                    <button className="p-0.5 hover:bg-gray-200 rounded transition-colors">
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="12"
                                        height="12"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="#7A7780"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      >
                                        <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                                        <path d="M21 3v5h-5" />
                                        <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                                        <path d="M8 16H3v5" />
                                      </svg>
                                    </button>
                                    <button className="p-0.5 hover:bg-gray-200 rounded transition-colors">
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="12"
                                        height="12"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="#7A7780"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      >
                                        <path d="M18 6 6 18"></path>
                                        <path d="m6 6 12 12"></path>
                                      </svg>
                                    </button>
                                  </div>
                                </motion.div>

                                {/* When tab is expanded - show video with gradient border */}
                                <motion.div
                                  className="absolute inset-0 rounded-lg overflow-hidden"
                                  style={{
                                    opacity:
                                      index === 0
                                        ? googleTabVideoOpacity
                                        : index === 1
                                        ? notionTabVideoOpacity
                                        : index === 2
                                        ? redditTabVideoOpacity
                                        : xTabVideoOpacity,
                                    background: `linear-gradient(#000, #000) padding-box,
                                    linear-gradient(180deg, #5D11F4 0%, #FF9000 47%, #33B3FF 100%) border-box`,
                                    border: "1.5px solid transparent",
                                    borderRadius: "8px",
                                    backgroundClip: "padding-box, border-box",
                                    boxShadow: "0px 1.335px 5.341px 0px rgba(74, 68, 89, 0.32)",
                                  }}
                                >
                                  <video
                                    ref={
                                      index === 0
                                        ? googleVideoRef
                                        : index === 1
                                        ? notionVideoRef
                                        : index === 2
                                        ? redditVideoRef
                                        : xVideoRef
                                    }
                                    className="w-full h-full object-cover"
                                    src={
                                      index === 0
                                        ? "https://fellou.s3.us-west-1.amazonaws.com/videos/website-main-page/Website+Hero/9.3+1.mov"
                                        : index === 1
                                        ? "https://fellou.s3.us-west-1.amazonaws.com/videos/website-main-page/Website+Hero/g-mail.mp4"
                                        : index === 2
                                        ? "https://fellou.s3.us-west-1.amazonaws.com/videos/website-main-page/Website+Hero/9%E6%9C%883%E6%97%A5+(1).mov"
                                        : "https://fellou.s3.us-west-1.amazonaws.com/videos/website-main-page/Website+Hero/1.mov"
                                    }
                                    loop
                                    muted
                                    playsInline
                                  />
                                </motion.div>
                              </>
                            )}
                            {tab.isNewTab && (
                              <div className="flex items-center justify-center w-full h-full">
                                <motion.span
                                  className="text-gray-600 text-base font-light absolute"
                                  style={{ opacity: plusTabHideSymbol }}
                                >
                                  +
                                </motion.span>
                                <motion.div
                                  className="flex items-center gap-2 w-full h-full"
                                  style={{ opacity: plusTabShowText }}
                                >
                                  <Image
                                    src="/favicon.ico"
                                    alt="Fellou"
                                    width={16}
                                    height={16}
                                    unoptimized
                                    className="shrink-0"
                                  />
                                  <span className="text-sm text-gray-700 truncate flex-1">shadow workspace</span>
                                </motion.div>
                              </div>
                            )}
                          </motion.div>
                          {/* Add separator line if not the last tab */}
                          {index < tabs.length - 1 && !tab.isNewTab && (
                            <div className="flex h-[0px] items-center justify-center relative shrink-0 w-[0px]">
                              <div className="flex-none rotate-[90deg]">
                                <div className="h-0 relative w-2">
                                  <div className="absolute bottom-0 left-0 right-0 top-[-1px] h-[1px] bg-gray-300/50"></div>
                                </div>
                              </div>
                            </div>
                          )}
                        </Fragment>
                      ))}
                    </div>
                  </div>

                  {/* Profile Button */}
                  <div className="flex items-center px-4">
                    <div className="bg-[rgba(255,255,255,0.56)] flex items-center justify-center p-[6px] rounded-[74.25px] w-6 h-6">
                      <span className="text-[10.5px] text-black leading-[12px]">💻</span>
                    </div>
                  </div>
                </motion.div>

                {/* Google Homepage Content */}
                <motion.div className="flex-1 bg-white overflow-hidden flex flex-col">
                  <div className="flex-1 flex items-start justify-center px-8 pt-32">
                    <motion.div
                      className="flex flex-col items-center w-full max-w-4xl"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.3 }}
                      style={{
                        opacity: googleContentOpacity,
                      }}
                    >
                      {/* Google Logo */}
                      {/* <motion.div
                        className="mb-8"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5, delay: 0.4 }}
                        style={{
                          opacity: logoOpacity,
                          scale: logoScale,
                        }}
                      >
                        <Image
                          src="https://api.iconify.design/logos:google.svg"
                          alt="Google"
                          width={80}
                          height={80}
                          className="h-20 w-auto"
                        />
                      </motion.div> */}
                      {/* Fellou logo - direct display without Chrome animation */}
                      <motion.div
                        key="fellou-logo"
                        className="w-[326px] h-[82px] flex items-center justify-center overflow-hidden mb-8"
                        initial={{
                          opacity: 0,
                          filter: "blur(4px)",
                          scale: 0.9,
                        }}
                        animate={{
                          opacity: backgroundReady ? 1 : 0,
                          filter: backgroundReady ? "blur(0px)" : "blur(4px)",
                          scale: backgroundReady ? 1 : 0.9,
                        }}
                        transition={{
                          delay: backgroundReady ? 1.4 : 0,
                          duration: 0.8,
                          ease: "easeOut",
                        }}
                      >
                        <Image
                          src="/logo.svg"
                          alt="Fellou CE"
                          width={326}
                          height={82}
                          className="w-full h-full object-cover"
                        />
                      </motion.div>

                      {/* Search Bar with Figma design */}
                      <motion.div
                        className="w-full max-w-[806px] mb-8"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.5 }}
                      >
                        <div className="bg-white rounded-[16px] border border-[#efeef0] p-4 shadow-[0px_2px_8px_0px_rgba(150,148,156,0.08)] flex flex-col gap-6">
                          {/* First row: Search icon and input */}
                          <div className="flex items-center gap-2">
                            <Search size={18} className="text-gray-400 shrink-0" />
                            <input
                              type="text"
                              className="flex-1 bg-transparent focus:outline-none text-base text-[#96949c] placeholder:text-[#96949c]"
                              placeholder="Scroll Down To Discover More"
                              disabled
                            />
                          </div>

                          {/* Second row: Deep Action and @ buttons */}
                          <div className="flex items-center gap-2">
                            <div className="flex items-center">
                              <button className="bg-[#f7f7f8] flex items-center gap-1 px-2 py-0.5 rounded-[8px] transition-colors h-full">
                                <svg
                                  width="18"
                                  height="18"
                                  viewBox="0 0 18 18"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="text-[#020204]"
                                >
                                  <path
                                    d="M7.95825 7.95825C8.06514 7.85139 8.20182 7.77932 8.35037 7.75148C8.49893 7.72365 8.65243 7.74134 8.79075 7.80225L15.42 10.719C15.5619 10.7815 15.6809 10.8864 15.7607 11.0193C15.8405 11.1522 15.8772 11.3065 15.8657 11.4611C15.8542 11.6157 15.795 11.7629 15.6964 11.8825C15.5978 12.0021 15.4646 12.0882 15.315 12.129L12.8115 12.8115L12.129 15.315C12.0882 15.4646 12.0021 15.5978 11.8825 15.6964C11.7629 15.795 11.6157 15.8542 11.4611 15.8657C11.3065 15.8772 11.1522 15.8405 11.0193 15.7607C10.8864 15.6809 10.7815 15.5619 10.719 15.42L7.8015 8.79C7.74085 8.6517 7.72337 8.49831 7.75134 8.3499C7.7793 8.2015 7.85142 8.06499 7.95825 7.95825ZM12.75 2.25C13.3467 2.25 13.919 2.48705 14.341 2.90901C14.7629 3.33097 15 3.90326 15 4.5V6.75C15 6.94891 14.921 7.13968 14.7803 7.28033C14.6397 7.42098 14.4489 7.5 14.25 7.5C14.0511 7.5 13.8603 7.42098 13.7197 7.28033C13.579 7.13968 13.5 6.94891 13.5 6.75V4.5C13.5 4.30109 13.421 4.11032 13.2803 3.96967C13.1397 3.82902 12.9489 3.75 12.75 3.75H4.5C4.30109 3.75 4.11032 3.82902 3.96967 3.96967C3.82902 4.11032 3.75 4.30109 3.75 4.5V12.75C3.75 12.9489 3.82902 13.1397 3.96967 13.2803C4.11032 13.421 4.30109 13.5 4.5 13.5H6.75C6.94891 13.5 7.13968 13.579 7.28033 13.7197C7.42098 13.8603 7.5 14.0511 7.5 14.25C7.5 14.4489 7.42098 14.6397 7.28033 14.7803C7.13968 14.921 6.94891 15 6.75 15H4.5C3.90326 15 3.33097 14.7629 2.90901 14.341C2.48705 13.919 2.25 13.3467 2.25 12.75V4.5C2.25 3.90326 2.48705 3.33097 2.90901 2.90901C3.33097 2.48705 3.90326 2.25 4.5 2.25H12.75ZM9.9525 9.95175L11.2387 12.8768L11.4773 12.0037C11.5115 11.8778 11.578 11.763 11.6702 11.6707C11.7624 11.5784 11.8771 11.5117 12.003 11.4773L12.8768 11.2387L9.95175 9.95175H9.9525Z"
                                    fill="currentColor"
                                  />
                                </svg>
                                <span className="text-[#020204] text-sm">Deep Action</span>
                              </button>
                            </div>

                            <button className="flex items-center justify-center p-[7px] rounded-[8px] transition-colors">
                              <svg
                                width="18"
                                height="18"
                                viewBox="0 0 18 18"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className="text-gray-400"
                              >
                                <g clipPath="url(#clip0_822_6243_2)">
                                  <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M3 9C3 7.4087 3.63214 5.88258 4.75736 4.75736C5.88258 3.63214 7.4087 3 9 3C13.0253 3 15.3338 6.33375 15.0045 9.2925C14.9145 10.107 14.676 10.5848 14.4645 10.8637C14.2537 11.1435 14.0362 11.2687 13.9147 11.3287C13.35 11.6115 13.065 11.4878 12.9525 11.3985C12.81 11.286 12.6735 11.0212 12.7395 10.623L12.744 10.599L13.1213 6.825C13.1386 6.65351 13.0963 6.48128 13.0016 6.33728C12.9069 6.19329 12.7654 6.08633 12.6011 6.03439C12.4367 5.98245 12.2595 5.98871 12.0992 6.05211C11.9389 6.11551 11.8054 6.23218 11.721 6.3825C11.3694 6.02334 10.9496 5.73812 10.4862 5.54363C10.0227 5.34914 9.52509 5.2493 9.0225 5.25C6.95925 5.25 5.25 6.92175 5.25 9C5.25 11.0805 6.966 12.75 9.0285 12.75C9.9375 12.75 10.791 12.423 11.4562 11.868C11.5875 12.138 11.775 12.3795 12.021 12.5745C12.7087 13.119 13.65 13.1385 14.586 12.6705C14.8387 12.5445 15.2685 12.288 15.6615 11.7682C16.0545 11.2478 16.3785 10.5068 16.4955 9.45825C16.917 5.6655 13.9747 1.5 9 1.5C4.85775 1.5 1.5 4.85775 1.5 9C1.5 13.1423 4.85775 16.5 9 16.5C10.1566 16.5015 11.2978 16.2345 12.3337 15.72C12.5104 15.6306 12.6445 15.475 12.7068 15.2871C12.7692 15.0993 12.7548 14.8944 12.6667 14.7171C12.5786 14.5398 12.424 14.4046 12.2366 14.3408C12.0492 14.2771 11.8442 14.29 11.6663 14.3768C10.7514 14.8306 9.73575 15.0434 8.71563 14.995C7.69551 14.9466 6.70462 14.6386 5.8368 14.1002C4.96898 13.5617 4.25296 12.8107 3.75653 11.9182C3.26011 11.0257 2.99971 10.0213 3 9ZM6.75 9C6.75 7.76475 7.773 6.75 9.0225 6.75C10.4625 6.75 11.5455 8.07525 11.253 9.465C11.0347 10.4985 10.0988 11.25 9.0285 11.25C7.776 11.25 6.75 10.2338 6.75 9Z"
                                    fill="currentColor"
                                  />
                                </g>
                                <defs>
                                  <clipPath id="clip0_822_6243_2">
                                    <rect width="18" height="18" fill="white" />
                                  </clipPath>
                                </defs>
                              </svg>
                            </button>
                          </div>
                        </div>
                      </motion.div>

                      {/* Try these tasks section */}
                      <motion.div
                        className="w-full max-w-[806px] mb-2"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.7 }}
                      >
                        <div className="flex items-center gap-4">
                          <p className="text-[#7a7780] text-base">Try these task:</p>
                          <div className="flex items-center gap-2">
                            {/* Action button with purple background */}
                            <button className="bg-[#f3f1ff] border border-[#d6d0ff] flex items-center gap-1 px-3 py-0.5 h-8 rounded-[99px] transition-colors">
                              <Activity size={16} className="text-[#5d11f4]" />
                              <span className="text-[#5d11f4] text-sm font-medium">Action</span>
                            </button>
                            {/* Other buttons */}
                            <button className="border border-[#efeef0] flex items-center gap-1 px-3 py-0.5 h-8 rounded-[99px] transition-colors hover:bg-gray-50">
                              <FileBarChart size={16} className="text-[#514e56]" />
                              <span className="text-[#514e56] text-sm">Report</span>
                            </button>
                            <button className="border border-[#efeef0] flex items-center gap-1 px-3 py-0.5 h-8 rounded-[99px] transition-colors hover:bg-gray-50">
                              <Globe size={16} className="text-[#514e56]" />
                              <span className="text-[#514e56] text-sm">Webpage</span>
                            </button>
                            <button className="border border-[#efeef0] flex items-center gap-1 px-3 py-0.5 h-8 rounded-[99px] transition-colors hover:bg-gray-50">
                              <FileText size={16} className="text-[#514e56]" />
                              <span className="text-[#514e56] text-sm">Summarize</span>
                            </button>
                            <button className="border border-[#efeef0] flex items-center gap-1 px-3 py-0.5 h-8 rounded-[99px] transition-colors hover:bg-gray-50">
                              <ImageIcon size={16} className="text-[#514e56]" />
                              <span className="text-[#514e56] text-sm">Image</span>
                            </button>
                            <button className="border border-[#efeef0] flex items-center gap-1 px-3 py-0.5 h-8 rounded-[99px] transition-colors hover:bg-gray-50">
                              <Music size={16} className="text-[#514e56]" />
                              <span className="text-[#514e56] text-sm">Music</span>
                            </button>
                          </div>
                          {/* Refresh button */}
                          <button className="flex items-center justify-center p-2 rounded-[8px] transition-colors hover:bg-gray-100">
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="#7a7780"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                              <path d="M21 3v5h-5" />
                              <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                              <path d="M8 16H3v5" />
                            </svg>
                          </button>
                        </div>
                      </motion.div>

                      {/* Task suggestions list */}
                      <motion.div
                        className="w-full max-w-[806px] flex flex-col gap-2"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.9 }}
                      >
                        <div className="bg-white rounded-[8px] border border-[#efeef0] px-4 py-2 transition-colors hover:bg-gray-50">
                          <p className="text-[#020204] text-sm">
                            Plan a 7-day San Francisco trip and book my flight and hotel for two days from now.
                          </p>
                        </div>
                        <div className="bg-white rounded-[8px] border border-[#efeef0] px-4 py-2 transition-colors hover:bg-gray-50">
                          <p className="text-[#020204] text-sm">
                            Give me the top three new business updates for NVIDIA and Oracle in the last 24 hours.
                          </p>
                        </div>
                        <div className="bg-[#f7f7f8] rounded-[8px] border border-[#efeef0] px-4 py-2 transition-colors hover:bg-gray-100">
                          <p className="text-[#020204] text-sm">
                            From my Notion notes, produce a clear weekly research update and e-mail Dr. Dominic.
                          </p>
                        </div>
                      </motion.div>
                    </motion.div>
                  </div>
                </motion.div>
              </motion.div>

              {/* Navigation Button Pill */}
              <motion.div
                className="absolute left-1/2 transform -translate-x-1/2 -mt-16"
                initial={{ opacity: 0, y: -10, filter: "blur(10px)", scale: 0.9 }}
                animate={{
                  opacity: showPill ? 1 : 0,
                  y: showPill ? 0 : -10,
                  scale: showPill ? 1 : 0.9,
                  filter: showPill ? "blur(0px)" : "blur(10px)",
                }}
                transition={{ duration: 0.3 }}
              >
                <div className="bg-white/10 backdrop-blur-sm text-white  p-1 pr-3 rounded-full text-sm font-medium flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full overflow-hidden shrink-0">
                    <Image
                      src="/playerone.png"
                      alt="Player One"
                      width={100}
                      height={100}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <TextMorph>{pillMessage}</TextMorph>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      <style jsx>{`
        .fellou-buddy-text::after {
          content: "";
          display: inline-block;
          width: 16px;
          height: 16px;
          background-image: url("/fellou-buddy.png");
          background-size: contain;
          background-repeat: no-repeat;
          margin-left: 4px;
          vertical-align: text-bottom;
        }
      `}</style>
    </>
  );
}
